import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { dashboardApi } from '../services/api';
import { LayoutDashboard, GripVertical, Eye, EyeOff, Save, ArrowUp, ArrowDown, Check, RefreshCw, Bot, TrendingUp, Bell, Sparkles } from 'lucide-react';

const ROLES = [
  { value: 'admin', label: 'Administrador' },
  { value: 'dir_comercial', label: 'Director Comercial' },
  { value: 'comercial', label: 'Agente Comercial' },
  { value: 'agente_libre', label: 'Agente Libre' },
  { value: 'admin_comercial', label: 'Admin Comercial' },
];

const CATEGORIA_ICON = {
  ventas: TrendingUp,
  productividad: Check,
  alertas: Bell,
  ia: Sparkles,
};

const CATEGORIA_COLOR = {
  ventas: 'bg-emerald-100 text-emerald-700',
  productividad: 'bg-blue-100 text-blue-700',
  alertas: 'bg-amber-100 text-amber-700',
  ia: 'bg-violet-100 text-violet-700',
};

const TAMANOS = ['quarter', 'third', 'half', 'full'];

export default function DashboardConfig() {
  const [rol, setRol] = useState('admin');
  const [registry, setRegistry] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [cambios, setCambios] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    const [reg, cfg] = await Promise.all([
      dashboardApi.widgetsRegistry(),
      dashboardApi.getConfig(rol),
    ]);
    setRegistry(reg);
    // Unir con widgets del registry (mostrar todos, incluyendo los no activos)
    const cfgMap = Object.fromEntries((cfg.widgets || []).map((w) => [w.widget_id, w]));
    const full = Object.entries(reg.widgets).map(([wid, meta], i) => {
      const existing = cfgMap[wid];
      return {
        widget_id: wid,
        nombre: meta.nombre,
        descripcion: meta.descripcion,
        categoria: meta.categoria,
        tamano_default: meta.tamano_default,
        roles_sugeridos: meta.roles_sugeridos,
        activo: existing ? existing.activo : (cfg.is_default && meta.roles_sugeridos.includes(rol)),
        orden: existing?.orden ?? i,
        tamano: existing?.tamano || meta.tamano_default,
      };
    });
    full.sort((a, b) => a.orden - b.orden);
    setWidgets(full);
    setCambios(false);
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [rol]);

  const moverWidget = (i, dir) => {
    const nuevo = [...widgets];
    const j = i + dir;
    if (j < 0 || j >= nuevo.length) return;
    [nuevo[i], nuevo[j]] = [nuevo[j], nuevo[i]];
    nuevo.forEach((w, idx) => (w.orden = idx));
    setWidgets(nuevo);
    setCambios(true);
  };

  const toggleActivo = (i) => {
    const nuevo = [...widgets];
    nuevo[i].activo = !nuevo[i].activo;
    setWidgets(nuevo);
    setCambios(true);
  };

  const cambiarTamano = (i, tam) => {
    const nuevo = [...widgets];
    nuevo[i].tamano = tam;
    setWidgets(nuevo);
    setCambios(true);
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      await dashboardApi.setConfig(
        rol,
        widgets.map((w) => ({
          widget_id: w.widget_id,
          activo: w.activo,
          orden: w.orden,
          tamano: w.tamano,
        })),
      );
      setCambios(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-config">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900 flex items-center gap-3">
            <LayoutDashboard className="w-7 h-7 text-brand-500" />
            Configuración del Dashboard
          </h1>
          <p className="text-ink-500 mt-1">Define qué widgets ve cada rol y en qué orden. Los cambios se aplican al instante.</p>
        </div>
        <div className="flex gap-2 items-center">
          <label className="text-sm text-ink-600">Rol:</label>
          <select
            value={rol}
            onChange={(e) => setRol(e.target.value)}
            className="border border-ink-200 rounded px-3 py-1.5 text-sm bg-white"
            data-testid="select-rol-dashboard"
          >
            {ROLES.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
          </select>
          <Button variant="outline" onClick={cargar} data-testid="btn-recargar-dashboard-config">
            <RefreshCw className="w-4 h-4 mr-2" /> Recargar
          </Button>
          <Button
            onClick={guardar}
            disabled={!cambios || guardando}
            className="bg-brand-500 hover:bg-brand-600 text-white"
            data-testid="btn-guardar-dashboard-config"
          >
            <Save className="w-4 h-4 mr-2" /> {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </div>

      {cambios && (
        <Card className="p-3 bg-amber-50 border-amber-200 text-sm text-amber-800" data-testid="cambios-pendientes">
          Hay cambios sin guardar. Pulsa <strong>Guardar</strong> para aplicarlos.
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="widgets-list">
        {widgets.map((w, i) => {
          const Icon = CATEGORIA_ICON[w.categoria] || Bot;
          return (
            <Card
              key={w.widget_id}
              className={`p-3 transition-all ${w.activo ? 'border-brand-300 bg-white' : 'border-ink-200 bg-ink-50/50 opacity-60'}`}
              data-testid={`widget-card-${w.widget_id}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex flex-col gap-1 pt-1">
                  <button onClick={() => moverWidget(i, -1)} disabled={i === 0} className="text-ink-400 hover:text-ink-700 disabled:opacity-30" data-testid={`btn-up-${w.widget_id}`}>
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button onClick={() => moverWidget(i, 1)} disabled={i === widgets.length - 1} className="text-ink-400 hover:text-ink-700 disabled:opacity-30" data-testid={`btn-down-${w.widget_id}`}>
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>
                <div className={`w-9 h-9 rounded-lg ${CATEGORIA_COLOR[w.categoria] || 'bg-ink-100'} flex items-center justify-center shrink-0`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-ink-900 text-sm">{w.nombre}</h3>
                    <Badge variant="outline" className="text-[10px]">{w.categoria}</Badge>
                  </div>
                  <p className="text-xs text-ink-500 mt-0.5">{w.descripcion}</p>
                  <div className="flex gap-1 mt-2 items-center text-xs">
                    <span className="text-ink-400 mr-1">Tamaño:</span>
                    {TAMANOS.map((t) => (
                      <button
                        key={t}
                        onClick={() => cambiarTamano(i, t)}
                        className={`px-2 py-0.5 rounded border text-xs ${w.tamano === t ? 'bg-brand-500 border-brand-500 text-white' : 'border-ink-200 text-ink-600 hover:border-ink-400'}`}
                        data-testid={`btn-tam-${w.widget_id}-${t}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleActivo(i)}
                  className={w.activo ? 'border-emerald-300 text-emerald-700' : ''}
                  data-testid={`btn-toggle-${w.widget_id}`}
                >
                  {w.activo ? <><Eye className="w-3.5 h-3.5 mr-1" /> Visible</> : <><EyeOff className="w-3.5 h-3.5 mr-1" /> Oculto</>}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-4 bg-brand-50/40 border-brand-200 text-sm text-ink-700">
        <strong>Cómo funciona:</strong> cada widget consume su propio endpoint. Activar un widget no implica que los datos existan — por ejemplo los widgets "IA — análisis de clientes" e "Inteligencia Municipal" se alimentarán cuando se conecten en los sprints S5+. Los widgets de <strong>Ventas operacionales</strong> y <strong>Bots IA</strong> ya funcionan con los datos reales importados.
      </Card>
    </div>
  );
}
